import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const Privacy = () => {
  useSEO({
    title: 'Privacy Policy — EpisodeKit',
    description: 'EpisodeKit privacy policy. How we collect, use, and protect your data — written in plain English.',
    path: '/privacy',
    image: '/og/episodekit-og-privacy.jpg',
    index: true,
  });
  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="noise-overlay" />
      <Navbar />

      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.h1 
            className="text-4xl md:text-5xl font-unbounded font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Privacy Policy
          </motion.h1>
          
          <motion.div
            className="prose prose-invert max-w-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <p className="text-neutral-400 mb-8">
              Last Updated: December 19, 2025
            </p>

            <div className="space-y-6 text-neutral-300">
              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">1. Introduction</h2>
                <p>
                  EpisodeKit ("we", "our", or "us") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, process, and safeguard your information when you use our service. We are committed to compliance with the EU General Data Protection Regulation (GDPR) and other applicable data protection laws.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-white mt-4 mb-2">Account Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Email address (required for account creation and communication)</li>
                  <li>Username or display name (if provided)</li>
                  <li>Password (encrypted and hashed - we never store passwords in plain text)</li>
                  <li>Account creation date and last login information</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">Content You Upload</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Audio files (podcast episodes you upload for processing)</li>
                  <li>File metadata (duration, format, file size, upload date)</li>
                  <li>Episode titles, descriptions, and settings (tone preferences)</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">Generated Content</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>AI-generated transcripts of your audio files</li>
                  <li>AI-generated show notes, blog posts, and social media snippets</li>
                  <li>Chapter markers and timestamps</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">Usage Data</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Processing history and timestamps</li>
                  <li>Monthly usage statistics (minutes of audio processed)</li>
                  <li>Feature usage patterns and preferences</li>
                  <li>Browser type, device information, and IP address</li>
                  <li>Log data and error reports for troubleshooting</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">Subscription Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Subscription plan (Free, Basic, Pro, or Unlimited)</li>
                  <li>Subscription status and renewal dates</li>
                  <li>Subscription ID provided by our payment processor</li>
                </ul>

                <p className="mt-4">
                  <strong>Important:</strong> EpisodeKit does NOT collect, process, or store any credit card details, payment information, or billing data. All payment processing is handled by our payment processor, Lemon Squeezy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">3. How We Use Your Information</h2>
                <p>
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide and improve our transcription and content generation service</li>
                  <li>Process your audio files using AI models (OpenAI Whisper and GPT)</li>
                  <li>Manage your account, subscription, and service access</li>
                  <li>Enforce usage limits and Fair Use Policy compliance</li>
                  <li>Respond to support requests and technical inquiries</li>
                  <li>Send important service updates, security alerts, and account notifications (we do not send marketing emails without your explicit consent)</li>
                  <li>Analyze service performance, reliability, and usage patterns to improve our platform</li>
                  <li>Detect and prevent fraud, abuse, and security threats</li>
                  <li>Comply with legal obligations and enforce our Terms of Service</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">4. Third-Party Services</h2>
                <p>
                  We use the following third-party services to provide and improve our service:
                </p>
                
                <h3 className="text-xl font-semibold text-white mt-4 mb-2">Lemon Squeezy (Payment Processing)</h3>
                <p>
                  Lemon Squeezy acts as our Merchant of Record and handles all subscription billing, payment processing, tax calculation (including VAT for EU customers), and refunds. When you subscribe to a paid plan:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your payment information is collected and processed by Lemon Squeezy, not by EpisodeKit</li>
                  <li>Lemon Squeezy may share limited subscription information with us (subscription ID, status, plan level) to manage your service access</li>
                  <li>Your payment data is subject to Lemon Squeezy's privacy policy: <a href="https://www.lemonsqueezy.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.lemonsqueezy.com/privacy</a></li>
                </ul>
                <p className="mt-3">
                  <strong>We do NOT receive or store your credit card details or payment information.</strong>
                </p>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">OpenAI (AI Processing)</h3>
                <p>
                  We use OpenAI's services for:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Audio transcription using OpenAI Whisper</li>
                  <li>Content generation using GPT models (show notes, blog posts, social snippets)</li>
                </ul>
                <p className="mt-3">
                  Your audio files are sent to OpenAI for processing. OpenAI's processing of data is governed by OpenAI's privacy policy, data processing terms, and applicable service agreements. We recommend reviewing OpenAI's privacy policy and API data usage policy for current information on how they handle customer data.
                </p>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">Cloud Hosting Providers</h3>
                <p>
                  We use secure cloud infrastructure providers to store your data and run our service. These providers implement industry-standard security measures, including encryption in transit and at rest, access controls, and regular security audits.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">5. Data Storage & Security</h2>
                <p>
                  <strong>Storage:</strong> Your data is stored on secure cloud servers with industry-standard security controls. Audio files and generated content are retained as long as your account is active and you choose to keep them.
                </p>
                <p className="mt-3">
                  <strong>Security Measures:</strong> We implement multiple layers of security to protect your data:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encrypted data transmission using HTTPS/TLS</li>
                  <li>Encrypted password storage using industry-standard hashing algorithms</li>
                  <li>Secure database access controls and authentication</li>
                  <li>Regular security monitoring and vulnerability assessments</li>
                  <li>Access logging and audit trails</li>
                  <li>Restricted access to production systems (need-to-know basis)</li>
                </ul>
                <p className="mt-3">
                  <strong>Important:</strong> While we implement strong security measures, no method of transmission or storage is 100% secure. We cannot guarantee absolute security, but we are committed to protecting your data using industry best practices.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">6. Data Retention</h2>
                <p>
                  We retain your data as follows:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Data:</strong> Retained while your account is active</li>
                  <li><strong>Audio Files:</strong> Stored until you delete them or close your account</li>
                  <li><strong>Generated Content:</strong> Stored until you delete it or close your account</li>
                  <li><strong>Usage Logs:</strong> Retained for up to 12 months for billing, support, and service improvement purposes</li>
                  <li><strong>Billing Records:</strong> Retained for up to 7 years to comply with legal and tax requirements</li>
                </ul>
                <p className="mt-3">
                  Upon account deletion, we will delete or anonymize your personal data within 90 days, except where we are required by law to retain certain information.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">7. We Do Not Sell Your Data</h2>
                <p>
                  <strong>We do not sell, rent, or trade your personal information or content to third parties for marketing purposes.</strong> We only share data with third-party service providers as necessary to provide the service (as described in the "Third-Party Services" section), and only under strict data processing agreements.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">8. Your Rights (GDPR & Data Protection)</h2>
                <p>
                  Under the EU GDPR and other applicable data protection laws, you have the following rights:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Right to Access:</strong> Request a copy of your personal data we hold</li>
                  <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data</li>
                  <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> Request deletion of your personal data</li>
                  <li><strong>Right to Data Portability:</strong> Request your data in a structured, machine-readable format</li>
                  <li><strong>Right to Restrict Processing:</strong> Request that we limit how we use your data</li>
                  <li><strong>Right to Object:</strong> Object to certain types of data processing</li>
                  <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for data processing where consent is the legal basis</li>
                  <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
                </ul>
                <p className="mt-3">
                  To exercise these rights, please contact us at the email address below. We will respond to your request within 30 days.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">9. International Data Transfers</h2>
                <p>
                  Your data may be transferred to and processed in countries outside the European Economic Area (EEA), including the United States, where our service providers are located. When we transfer data internationally, we ensure appropriate safeguards are in place, such as:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Standard Contractual Clauses approved by the European Commission</li>
                  <li>Service providers' compliance with data protection frameworks</li>
                  <li>Appropriate technical and organizational security measures</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">10. Cookies & Tracking</h2>
                <p>
                  We use essential cookies and similar technologies to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Maintain your login session and authentication</li>
                  <li>Remember your preferences and settings</li>
                  <li>Analyze site performance and usage patterns (anonymized data)</li>
                  <li>Detect and prevent security threats and abuse</li>
                </ul>
                <p className="mt-3">
                  You can control cookies through your browser settings, but disabling essential cookies may affect service functionality. We do not use third-party advertising or tracking cookies.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">11. Children's Privacy</h2>
                <p>
                  Our service is not intended for users under 16 years of age (or the minimum age of digital consent in your jurisdiction). We do not knowingly collect personal data from children. If you believe we have inadvertently collected data from a child, please contact us immediately and we will delete it.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">12. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or service features. We will notify you of material changes by:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Email notification to your registered email address</li>
                  <li>Prominent notice on our website or in your account dashboard</li>
                </ul>
                <p className="mt-3">
                  Your continued use of the service after notification constitutes acceptance of the updated Privacy Policy. We encourage you to review this policy periodically.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">13. Contact Us</h2>
                <p>
                  For questions about this Privacy Policy, to exercise your data protection rights, or for any privacy-related concerns, contact us at:
                </p>
                <p className="mt-2">
                  <a href="mailto:support@episodekit.net" className="text-primary hover:underline">
                    support@episodekit.net
                  </a>
                </p>
                <p className="mt-4">
                  We are committed to addressing your privacy concerns and will respond to all requests in accordance with applicable data protection laws.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Privacy;